/**
 * FunnelBars — the cumulative highest-stage funnel as tapering SVG trapezoids.
 *
 * Each rung is "candidates who reached AT LEAST this stage", so the series is
 * monotone by construction and the taper is real rather than cosmetic. The
 * percentage beside each rung is its share of the WIDEST rung — the conversion
 * figure, which is the number a recruiter actually reads off this widget.
 *
 * Widths use a square-root scale, mirroring the v38 prototype: a search where
 * 970 reached Identified and 42 reached Client Interview would render the bottom
 * rung as a 2%-wide sliver on a linear scale, illegible and unlabelable. The
 * floor keeps the narrowest rung wide enough to hold its own text.
 *
 * Geometry is in `points` / `viewBox` ATTRIBUTES, never a `style` prop.
 *
 * THE SVG CARRIES NO TEXT AT ALL — labels inside a tapering polygon clip
 * unpredictably at narrow widths — which is precisely what makes a hover tooltip
 * worth having here. Without it the only way to learn which stage a rung is, is
 * to count rows in the list below and match by position. The list is still the
 * accessible representation and still holds every number, so the tooltip adds
 * reach rather than information.
 *
 * THE NOT-A-CANDIDATE FILTER LIVES INSIDE THIS COMPONENT, for the reason
 * spelled out at the top of `StageDonut.tsx`: `export/ReportDocument.tsx`
 * renders these components so the downloaded document mirrors the page by
 * construction, and a filter applied by one caller and not the other quietly
 * broke that. Callers pass the wire value unfiltered.
 *
 * It is also why the key below carries a COLOUR SWATCH as of 2026-08-27. A key
 * with no swatch, beside a chart with no labels, is not a key — it is a second
 * list that happens to be in the same order. The swatch comes off the same
 * palette the polygons do, so the two cannot drift apart.
 */

import type { SearchReportFunnelStep } from "@nu-assist/contracts";

import { stageSeriesStyle } from "../../../../components/stages/chartPalette";
import { ChartTooltip } from "./ChartTooltip";
import { SeriesSwatch } from "./SeriesSwatch";
import { StageHatchDefs, StageSwatchDot, hatchFill, useHatchScope } from "./StageHatch";
import { CHART_VIEW_WIDTH, FUNNEL_ROW_GAP, FUNNEL_ROW_HEIGHT } from "./chartLayout";
import { candidateFunnelSteps } from "./seriesDefaults";
import { pointerSide, useHoverPoint } from "./useHoverPoint";

export interface FunnelBarsProps {
  /**
   * The report's own `highest_stage_funnel`, UNFILTERED.
   *
   * Pass the wire value. This component drops the not-a-candidate rungs and
   * re-bases `pct_of_first` onto the new widest rung itself — see the note
   * above for why that lives here rather than at the call site.
   */
  steps: readonly SearchReportFunnelStep[];
  /** Shown when no candidate reached a ladder stage. */
  emptyMessage: string;
}

const WIDTH = CHART_VIEW_WIDTH;
const ROW_HEIGHT = FUNNEL_ROW_HEIGHT;
const ROW_GAP = FUNNEL_ROW_GAP;
/** Narrowest rung, as a fraction of the full width. */
const MIN_WIDTH_FRACTION = 0.38;

export function FunnelBars({ steps: wireSteps, emptyMessage }: FunnelBarsProps) {
  const hover = useHoverPoint();
  // Filtered and re-based HERE — see the header note. `Source` is rung 1 and
  // therefore normally the widest, so dropping it without re-basing would leave
  // every remaining rung quoting a share of a number no longer on the page.
  const steps = candidateFunnelSteps(wireSteps);
  // No palette prop: `stageSeriesStyle` is a function of the stage name, so the
  // donut beside this widget and the line charts one tab over reach the same
  // answer without anything being threaded through the page. See
  // `chartPalette.ts` for why a set-scoped assignment could not.
  const scope = useHatchScope();

  if (steps.length === 0) {
    // The caller's `emptyMessage` describes a search that never progressed. It
    // is the WRONG sentence for a search the filter emptied — every candidate
    // stopped at `Source`, which really is rung 1, so "no candidate progressed"
    // reassures the reader while the data is being withheld. N1, iter-2 review
    // of PR #797. Reachable well short of an all-excluded search: any search
    // where nobody got past `Source` lands here.
    const filteredEverything = wireSteps.length > 0;
    return (
      <p data-testid="funnel-empty" className="text-body text-text-secondary">
        {/* "leaves out", not "excludes" — the funnel omits two different
            populations and only one of them is excluded. See the matching note
            in `domain_export.py`; N3, iter-3 review of PR #797. */}
        {filteredEverything
          ? "No candidate on this search reached a rung this funnel draws — they are all at stages it leaves out, whether excluded as non-candidates or off the ladder entirely."
          : emptyMessage}
      </p>
    );
  }

  const widest = Math.max(...steps.map((s) => s.count), 1);
  const widths = steps.map((step) => {
    const scaled = Math.sqrt(step.count / widest);
    return WIDTH * (MIN_WIDTH_FRACTION + scaled * (1 - MIN_WIDTH_FRACTION));
  });
  const height = steps.length * (ROW_HEIGHT + ROW_GAP) - ROW_GAP;

  return (
    <div data-testid="funnel-bars">
      {/* The tooltip's positioning context — the SVG's own box, so the bubble
          lands over the funnel rather than over the list below it. */}
      <div className="relative">
        <svg
          viewBox={`0 0 ${WIDTH} ${height}`}
          role="presentation"
          aria-hidden="true"
          className="block w-full"
          onPointerLeave={hover.hide}
        >
          <StageHatchDefs scope={scope} />
          {steps.map((step, i) => {
            const y = i * (ROW_HEIGHT + ROW_GAP);
            const topWidth = widths[i];
            // The last rung tapers to 70% of itself so the funnel closes rather
            // than ending on a blunt rectangle.
            const bottomWidth = i < steps.length - 1 ? widths[i + 1] : topWidth * 0.7;
            const topLeft = (WIDTH - topWidth) / 2;
            const topRight = (WIDTH + topWidth) / 2;
            const bottomLeft = (WIDTH - bottomWidth) / 2;
            const bottomRight = (WIDTH + bottomWidth) / 2;
            return (
              <polygon
                key={step.stage}
                points={[
                  `${topLeft},${y}`,
                  `${topRight},${y}`,
                  `${bottomRight},${y + ROW_HEIGHT}`,
                  `${bottomLeft},${y + ROW_HEIGHT}`,
                ].join(" ")}
                onPointerEnter={(event) => hover.show(i, pointerSide(event))}
                className={`${stageSeriesStyle(step.stage).fillClass} ${
                  hover.index === null || hover.index === i ? "" : "opacity-60"
                }`}
              />
            );
          })}
          {/* The hatch layer — see the note in `StageDonut`. The funnel draws
              the eight ladder rungs, which are all tier 0, so this normally
              renders nothing; it exists because the rungs come off the wire and
              a backend that ever reported an off-ladder step would otherwise
              paint it as a silent duplicate of a rung. Drawn after every
              polygon so the base shapes keep one-per-rung order, and
              `pointer-events-none` so the overlay never eats a hover. */}
          <g className="pointer-events-none">
            {steps.map((step, i) => {
              const { tier } = stageSeriesStyle(step.stage);
              if (tier === 0) return null;
              const y = i * (ROW_HEIGHT + ROW_GAP);
              const topWidth = widths[i];
              const bottomWidth = i < steps.length - 1 ? widths[i + 1] : topWidth * 0.7;
              return (
                <polygon
                  key={step.stage}
                  points={[
                    `${(WIDTH - topWidth) / 2},${y}`,
                    `${(WIDTH + topWidth) / 2},${y}`,
                    `${(WIDTH + bottomWidth) / 2},${y + ROW_HEIGHT}`,
                    `${(WIDTH - bottomWidth) / 2},${y + ROW_HEIGHT}`,
                  ].join(" ")}
                  fill={hatchFill(scope, tier)}
                  className={hover.index === null || hover.index === i ? "" : "opacity-60"}
                />
              );
            })}
          </g>
        </svg>

        {hover.index !== null && steps[hover.index] ? (
          <ChartTooltip
            testId="funnel-tooltip"
            title={steps[hover.index].stage}
            rows={[
              {
                key: "count",
                label: "Reached",
                value: String(steps[hover.index].count),
                // The palette's own `stroke`, not the fill class with `fill-`
                // swapped for `stroke-`: Tailwind cannot see a class built at
                // runtime. Both come off one assignment, so the swatch matches
                // its rung by construction.
                swatch: (
                  <SeriesSwatch
                    strokeClass={stageSeriesStyle(steps[hover.index].stage).strokeClass}
                    dash={stageSeriesStyle(steps[hover.index].stage).dash}
                  />
                ),
              },
              {
                key: "pct",
                label: "Of the widest stage",
                value: `${steps[hover.index].pct_of_first}%`,
              },
            ]}
            side={hover.side}
          />
        ) : null}
      </div>

      {/* The accessible representation, and — because the SVG carries no text
          at all — the ONLY way to learn which rung is which. That is what makes
          the swatch load-bearing rather than decorative: without it a reader
          matches a band to a name by counting rows and hoping, which is how the
          cyan `Source` band came to read as part of `Identified`.

          Jared, 2026-08-25: *"source should have a little blue dot then number
          and then just say percent. We don't need of the widest stage… color
          dot word number percent just like this here also"* — "this" being the
          Current Stage legend beside it, whose shape this now mirrors line for
          line. The words it drops are not lost: the panel's info tooltip says
          what the percentage is a share of, and so does the hover bubble. */}
      <ul data-testid="funnel-legend" className="mt-3 flex flex-col gap-1">
        {steps.map((step) => (
          <li key={step.stage} className="flex items-center gap-2 text-caption text-text-secondary">
            <StageSwatchDot
              bgClass={stageSeriesStyle(step.stage).bgClass}
              tier={stageSeriesStyle(step.stage).tier}
            />
            <span>{step.stage}</span>
            <strong className="text-text">{step.count}</strong>
            <span>({step.pct_of_first}%)</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
